import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { 
  Image, Building2, FolderKanban, Smartphone, 
  Newspaper, Users, Briefcase, Mail, Eye
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  getSliders, getCompanies, getProjects, getApplications,
  getNews, getBoardMembers, getJobs, getContacts 
} from '../../lib/api';

const AdminDashboard = () => {
  const { t } = useLanguage();
  const [stats, setStats] = useState({
    sliders: 0,
    companies: 0,
    projects: 0,
    applications: 0,
    news: 0,
    boardMembers: 0,
    jobs: 0,
    contacts: 0,
    unreadContacts: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const [
          slidersRes, companiesRes, projectsRes, appsRes,
          newsRes, boardRes, jobsRes, contactsRes
        ] = await Promise.all([
          getSliders(),
          getCompanies(),
          getProjects(),
          getApplications(),
          getNews(),
          getBoardMembers(),
          getJobs(),
          getContacts()
        ]);

        setStats({
          sliders: slidersRes.data.length,
          companies: companiesRes.data.length,
          projects: projectsRes.data.length,
          applications: appsRes.data.length,
          news: newsRes.data.length,
          boardMembers: boardRes.data.length,
          jobs: jobsRes.data.length,
          contacts: contactsRes.data.length,
          unreadContacts: contactsRes.data.filter(c => !c.is_read).length
        });
      } catch (error) {
        console.error('Error loading stats:', error);
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, []);

  const statCards = [
    { 
      title: { ar: 'الشرائح', en: 'Sliders' }, 
      value: stats.sliders, 
      icon: Image, 
      link: '/admin/sliders',
      color: '#B76E79'
    },
    { 
      title: { ar: 'الشركات', en: 'Companies' }, 
      value: stats.companies, 
      icon: Building2, 
      link: '/admin/companies',
      color: '#D4AF37'
    },
    { 
      title: { ar: 'المشاريع', en: 'Projects' }, 
      value: stats.projects, 
      icon: FolderKanban, 
      link: '/admin/projects',
      color: '#10B981'
    },
    { 
      title: { ar: 'التطبيقات', en: 'Applications' }, 
      value: stats.applications, 
      icon: Smartphone, 
      link: '/admin/applications',
      color: '#3B82F6'
    },
    { 
      title: { ar: 'الأخبار', en: 'News' }, 
      value: stats.news, 
      icon: Newspaper, 
      link: '/admin/news',
      color: '#F59E0B'
    },
    { 
      title: { ar: 'مجلس الإدارة', en: 'Board Members' }, 
      value: stats.boardMembers, 
      icon: Users, 
      link: '/admin/board',
      color: '#8B5CF6'
    },
    { 
      title: { ar: 'الوظائف', en: 'Jobs' }, 
      value: stats.jobs, 
      icon: Briefcase, 
      link: '/admin/jobs',
      color: '#EC4899'
    },
    { 
      title: { ar: 'الرسائل', en: 'Messages' }, 
      value: stats.contacts, 
      icon: Mail, 
      link: '/admin/contacts',
      color: '#EF4444',
      badge: stats.unreadContacts > 0 ? stats.unreadContacts : null
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-raad-rose border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">
          {t('لوحة التحكم', 'Dashboard')}
        </h1>
        <p className="text-gray-400">
          {t('نظرة عامة على محتوى الموقع', 'Overview of website content')}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.link}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Link to={stat.link}>
                <Card className="bg-raad-card border-white/10 hover:border-white/30 transition-all group">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-gray-400">
                      {t(stat.title.ar, stat.title.en)}
                    </CardTitle>
                    <div 
                      className="p-2 rounded-lg relative"
                      style={{ backgroundColor: stat.color + '20' }}
                    >
                      <Icon className="w-5 h-5" style={{ color: stat.color }} />
                      {stat.badge && (
                        <span className="absolute -top-1 -end-1 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
                          {stat.badge}
                        </span>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-end justify-between">
                      <span className="text-3xl font-bold text-white">{stat.value}</span>
                      <span className="text-gray-500 text-sm group-hover:text-raad-gold transition-colors flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        {t('عرض', 'View')}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="bg-raad-card border-white/10">
          <CardHeader>
            <CardTitle className="text-white">
              {t('إجراءات سريعة', 'Quick Actions')}
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <Link
              to="/admin/sliders"
              className="p-4 rounded-xl bg-white/5 hover:bg-white/10 text-center transition-colors"
            >
              <Image className="w-8 h-8 text-raad-rose mx-auto mb-2" />
              <span className="text-white text-sm">{t('إضافة شريحة', 'Add Slider')}</span>
            </Link>
            <Link
              to="/admin/news"
              className="p-4 rounded-xl bg-white/5 hover:bg-white/10 text-center transition-colors"
            >
              <Newspaper className="w-8 h-8 text-raad-gold mx-auto mb-2" />
              <span className="text-white text-sm">{t('إضافة خبر', 'Add News')}</span>
            </Link>
            <Link
              to="/admin/jobs"
              className="p-4 rounded-xl bg-white/5 hover:bg-white/10 text-center transition-colors"
            >
              <Briefcase className="w-8 h-8 text-purple-500 mx-auto mb-2" />
              <span className="text-white text-sm">{t('إضافة وظيفة', 'Add Job')}</span>
            </Link>
            <Link
              to="/"
              target="_blank"
              className="p-4 rounded-xl bg-white/5 hover:bg-white/10 text-center transition-colors"
            >
              <Eye className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <span className="text-white text-sm">{t('عرض الموقع', 'View Site')}</span>
            </Link>
          </CardContent>
        </Card>

        <Card className="bg-raad-card border-white/10">
          <CardHeader>
            <CardTitle className="text-white">
              {t('معلومات النظام', 'System Info')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">{t('إصدار النظام', 'System Version')}</span>
              <span className="text-white">1.0.0</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">{t('اللغات المدعومة', 'Supported Languages')}</span>
              <span className="text-white">{t('العربية، الإنجليزية', 'Arabic, English')}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">{t('آخر تحديث', 'Last Update')}</span>
              <span className="text-white">{new Date().toLocaleDateString()}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
